import { useState, useEffect, useMemo, useRef } from "react";
import { FaChevronDown } from "react-icons/fa";

const SelectInputWithSearch = ({
  className,
  label,
  placeholder = "Enter or Select ...",
  options = [],           // array of { value, label }
  name,
  value,                  // selected value (string)
  onChange,               // receives { target: { name, value } }
  mandatory,
  required = false,
  error = "",
  primary = false,
  primaryChecked = false,
  border,
  onPrimaryChange = () => {},
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [addedOptions, setAddedOptions] = useState([]); // temporary user-added options
  const containerRef = useRef(null);

  // merged options (built-in + user-added)
  const mergedOptions = useMemo(() => [...options, ...addedOptions], [options, addedOptions]);

  // filtered options shown while typing
  const filteredOptions = useMemo(() => {
    if (!search) return mergedOptions;
    return mergedOptions.filter((opt) =>
      opt.label.toLowerCase().includes(search.toLowerCase())
    );
  }, [mergedOptions, search]);

  // Derived temp option (when typing and no exact match)
  const tempOption = useMemo(() => {
    if (!search) return null;
    const exact = mergedOptions.some((o) => o.label.toLowerCase() === search.toLowerCase());
    if (!exact) {
      return { value: search.toLowerCase().replace(/\s+/g, "_"), label: search };
    }
    return null;
  }, [search, mergedOptions]);

  // Display logic:
  // - while typing show search
  // - if dropdown open and search empty => show empty (prevents auto-refill while editing)
  // - otherwise show selected option label (when dropdown closed)
  const displayedValue = useMemo(() => {
    if (search !== "") return search;
    if (isOpen && search === "") return "";
    const sel = mergedOptions.find((o) => o.value === value);
    return sel ? sel.label : value;
  }, [search, isOpen, value, mergedOptions]);

  // Close when clicked outside
  useEffect(() => {
    const onDocClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearch(""); // hide typing and temp option when clicking outside
      }
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, []);

  // When user types
  const handleInputChange = (e) => {
    const v = e.target.value;
    setSearch(v);

    // If user cleared the input manually, clear parent selection and
    // remove any addedOption that matched the previous selected value
    if (v === "") {
      // Inform parent selection cleared
      onChange?.({ target: { name, value: "" } });

      // If the previous selected value was one of our addedOptions, remove it
      setAddedOptions((prev) => {
        if (!value) return prev;
        const exists = prev.some((p) => p.value === value);
        if (!exists) return prev;
        return prev.filter((p) => p.value !== value);
      });
    }
  };

  // Select an option (by option.value)
  const handleSelect = (selectedValue) => {
    onChange?.({ target: { name, value: selectedValue } });
    setIsOpen(false);
    setSearch("");
  };

  // Add derived temp option and select it
  const handleAddTempAndSelect = () => {
    if (!tempOption) return;
    // If temp option value already present in addedOptions, don't duplicate
    setAddedOptions((prev) => {
      if (prev.some((p) => p.value === tempOption.value)) return prev;
      return [...prev, tempOption];
    });
    handleSelect(tempOption.value);
  };

  // Key handling (Enter to select/add)
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      // If exact match exists -> select existing option
      const exactMatch = mergedOptions.find(
        (o) => o.label.toLowerCase() === (search || "").toLowerCase()
      );
      if (exactMatch) {
        handleSelect(exactMatch.value);
        return;
      }
      // else if tempOption present -> add + select
      if (tempOption) {
        handleAddTempAndSelect();
      }
    }
    // (Optional future: handle ArrowUp/Down to navigate)
  };

  return (
    <div ref={containerRef} className="mb-3 relative">
      {label && (
        <div className="flex justify-between items-center gap-2">
          <label className="block mb-2 text-gray-700 font-medium text-[14px]">
            {label}{" "}
            <sup className="text-red-500 text-sm">{mandatory ? "*" : ""}</sup>
          </label>

          {primary && (
            <div className="flex gap-2 items-center px-2">
              <input
                className="w-4 h-4"
                type="checkbox"
                id={`primary_${name ?? "select"}`}
                checked={primaryChecked}
                onChange={(ev) => {
                  ev.stopPropagation();
                  onPrimaryChange(ev.target.checked);
                }}
                onClick={(ev) => ev.stopPropagation()}
              />
              <label
                htmlFor={`primary_${name ?? "select"}`}
                className="text-xs select-none"
                onClick={(ev) => ev.stopPropagation()}
              >
                Set as Primary
              </label>
            </div>
          )}
        </div>
      )}

      <div className="relative">
        <input
          type="text"
          className={`${className} ${border ? "border" : "shadow"} rounded-lg py-3 px-3 w-full text-sm`}
          placeholder={`Enter or Select ${placeholder}`}
          value={displayedValue}
          onFocus={() => setIsOpen(true)}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          required={required}
        />
        <FaChevronDown
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 cursor-pointer"
          onClick={() => setIsOpen((s) => !s)}
        />
      </div>

      {isOpen && (
        <div className={`absolute z-10 w-full bg-white ${border ? " border" : "shadow"} border-gray-300 rounded-lg max-h-60 overflow-auto shadow-lg mt-1`}>
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option) => (
              <div
                key={option.value}
                onClick={() => handleSelect(option.value)}
                className="px-4 py-2 hover:bg-gradient-to-r from-[#ECF1FF] to-[#EED8FF] cursor-pointer text-sm"
              >
                {option.label}
              </div>
            ))
          ) : tempOption ? (
            <div
              className="px-4 py-2 bg-primary-500 text-white cursor-pointer text-sm"
              onClick={handleAddTempAndSelect}
            >
                Select "{tempOption.label}"
            </div>
          ) : (
            <div className="px-4 py-2 text-sm text-gray-500">No options</div>
          )}
        </div>
      )}

      {error && (
        <div className="w-full bg-red-100 rounded-md h-auto mt-1">
          <p className="text-red-500 text-sm ml-3">{error}</p>
        </div>
      )}
    </div>
  );
};

export default SelectInputWithSearch;


